import React, { useEffect } from 'react';
import { logout } from '../../domains/auth/services/auth.service';
import { LayoutDashboard, Users, Database, BarChart3, Settings, LogOut, X, Clock, Network, Building2, MessageSquare, ChevronLeft, Plus, Search, Key } from 'lucide-react';
import { 
  isAdmin, 
  isAnalyst, 
  isClient, 
  isInternal, 
  isExternal, 
  getRoleDisplayName, 
  User,
  canViewDataSources,
  canViewUsers,
  canViewActivity,
  canManageCompanies
} from '../../utils/permissions';
import { useUIStore } from '../state/uiStore';

type Props = {
  currentUser: any;
  currentView: string;
  setCurrentView: (view: string) => void;
  setCurrentUser: (u: any) => void;
};

const Sidebar: React.FC<Props> = ({ currentUser, currentView, setCurrentView, setCurrentUser }) => {
  const { sidebarOpen, setSidebarOpen } = useUIStore();
  // close sidebar on Escape for keyboard accessibility
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && sidebarOpen) setSidebarOpen(false);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [sidebarOpen, setSidebarOpen]);

  const user = currentUser as User;
  const displayRole = getRoleDisplayName(currentUser?.role);
  
  // Menu items with security-based visibility
  const menuItems = [
    // Admin overview - only for internal admins
    { 
      icon: BarChart3, 
      label: 'Panel Admin', 
      view: 'admin-overview',
      visible: isAdmin(user) && isInternal(user)
    },
    
    // Panel Empresa - only for external users (summary/overview)
    { 
      icon: Building2, 
      label: 'Panel Empresa', 
      view: 'client-dashboard',
      visible: isExternal(user)
    },
    
    // Dashboards - everyone can see (dashboard management)
    { 
      icon: LayoutDashboard, 
      label: 'Mis Dashboards', 
      view: 'dashboards',
      visible: true
    },
    
    // EXTERNAL users see combined user management for their empresa
    { 
      icon: Users, 
      label: 'Usuarios', 
      view: 'users-external',
      visible: isExternal(user) && canViewUsers(user)
    },
    
    // Users Internal - only Controller General (ROOT)
    { 
      icon: Users, 
      label: 'Usuarios Internos', 
      view: 'users-internal',
      visible: canViewUsers(user) && isAdmin(user) && isInternal(user)
    },
    
    // Users External - internal admins only (EXTERNAL users see above combined view)
    { 
      icon: Users, 
      label: 'Usuarios Externos', 
      view: 'users-external',
      visible: canViewUsers(user) && isInternal(user)
    },
    
    // Data Sources - INTERNAL + EXTERNAL admins (filtered by empresaId)
    { 
      icon: Database, 
      label: 'Fuentes de Datos', 
      view: 'datasources',
      visible: canViewDataSources(user)
    },
    
    // API Credentials - INTERNAL + EXTERNAL admins
    { 
      icon: Key, 
      label: 'Credenciales API', 
      view: 'credentials',
      visible: canViewDataSources(user)
    },
    
    /* 
    // Data Modeling - Hefesto analytical modeling
    { 
      icon: Network, 
      label: 'Modelado Analítico', 
      view: 'data-modeling',
      visible: (isInternal(user) && (isAdmin(user) || isAnalyst(user))) || 
               (isExternal(user) && isAdmin(user) && user.empresaId)
    },
    */  
    // Chat Analítico - Conversational data analysis
    { 
      icon: MessageSquare, 
      label: 'Chat Analítico', 
      view: 'chat-analitico',
      visible: (isInternal(user) && (isAdmin(user) || isAnalyst(user))) || 
               (isExternal(user) && (isAdmin(user) || isAnalyst(user)) && user.empresaId)
    },
    
    // Companies - only Controller General (ROOT)
    { 
      icon: Building2, 
      label: 'Empresas', 
      view: 'companies',
      visible: canManageCompanies(user)
    },
    
    // Activity logs - internal admins + external admins
    { 
      icon: Clock, 
      label: 'Actividad', 
      view: 'activity',
      visible: canViewActivity(user)
    },
    
    // Settings - only internal admins
    { 
      icon: Settings, 
      label: 'Configuración', 
      view: 'settings',
      visible: isAdmin(user) && isInternal(user)
    }
  ].filter(item => item.visible); // Only show items user has permission to see

  return (
    <>
      {/* backdrop to dim content and provide clear separation; clicking it closes the sidebar */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-40 transition-opacity"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}

      <div className={`fixed top-0 left-0 h-full w-72 bg-slate-800 border-r border-slate-700 transform transition-transform duration-300 z-50 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'} shadow-2xl flex flex-col`}>
        {/* close control inside sidebar */}
        <button onClick={() => setSidebarOpen(false)} aria-label="Cerrar sidebar" className="absolute top-3 right-3 p-1 rounded-md text-slate-300 hover:bg-slate-700 z-60">
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="p-4 border-b border-slate-700">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-sky-500 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-white font-bold text-base">AION BI</h2>
              <p className="text-xs text-slate-400">Dashboard System</p>
            </div>
          </div>
        </div>

        {/* Menu Principal */}
        <div className="flex-1 overflow-y-auto px-3 py-4">
          <nav className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isDev = (item as any).isDev;
              return (
                <button
                  key={item.view}
                  onClick={() => setCurrentView(item.view)}
                  className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm ${
                    currentView === item.view 
                      ? 'bg-sky-500 text-white font-medium' 
                      : 'text-slate-300 hover:bg-slate-700'
                  } ${isDev ? 'border border-dashed border-sky-500/50' : ''}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* User Footer */}
        <div className="border-t border-slate-700 p-3">
          <div className="flex items-center gap-3 px-2 py-2 mb-2">
            <div className="w-9 h-9 bg-sky-500 rounded-full flex items-center justify-center text-white font-semibold">
              {currentUser?.name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-white truncate">{currentUser?.name || 'Usuario'}</div>
              <div className="text-xs text-slate-400 truncate">{getRoleDisplayName(currentUser?.role)}</div>
            </div>
          </div>
          <button
            onClick={() => { logout(); setCurrentView('login'); setCurrentUser(null); }}
            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-slate-700 rounded-lg transition-colors text-sm text-slate-400"
          >
            <LogOut className="w-4 h-4" />
            <span>Cerrar Sesión</span>
          </button>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
